import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, ImageIcon, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import { uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

const OG_W = 1200;
const OG_H = 630;
const OG_ASPECT = OG_W / OG_H;

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

// Always outputs an exact 1200x630 JPEG (the Open Graph spec size).
async function getOgBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = OG_W;
  canvas.height = OG_H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, OG_W, OG_H);
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, OG_W, OG_H
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

/**
 * Dedicated Open Graph image uploader — fixed to a 1200×630 crop & output.
 * Intentionally separate from the shared MediaUploader so it can't affect
 * profile/cover uploads. value/onChange store the /api/files/... URL.
 */
export function OgImageUploader({ value, onChange, label = "Open Graph image (1200×630)", testId = "og-image" }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const chk = validateUpload(f, "seo_og");
    if (!chk.ok) { toast.error(chk.message); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result); setOpen(true); setZoom(1); setCrop({ x: 0, y: 0 }); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);
  const save = async () => {
    setBusy(true);
    try {
      const blob = await getOgBlob(src, pixels);
      const url = await uploadFile(new File([blob], "og-image.jpg", { type: "image/jpeg" }), "seo_og");
      onChange(url);
      setOpen(false);
    } catch {
      toast.error("Could not upload image.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testId}>
      {label && <Label className="mb-1.5 block">{label}</Label>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onSelect} data-testid={`${testId}-input`} />
      {value ? (
        <div className="group relative block w-full max-w-sm">
          <img src={fileUrl(value)} alt={label} className="w-full border border-border object-cover" style={{ aspectRatio: OG_ASPECT }} />
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Button type="button" size="sm" variant="secondary" onClick={() => inputRef.current?.click()} data-testid={`${testId}-replace`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="destructive" onClick={() => onChange(null)} data-testid={`${testId}-remove`}><X className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          className="flex w-full max-w-sm flex-col items-center justify-center gap-1.5 border border-dashed border-border bg-secondary/40 text-muted-foreground hover:bg-secondary"
          style={{ aspectRatio: OG_ASPECT }} data-testid={`${testId}-upload`}>
          <ImageIcon className="h-5 w-5" />
          <span className="text-xs">Upload 1200×630</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Crop to 1200×630</DialogTitle></DialogHeader>
          <div className="relative h-64 w-full overflow-hidden rounded-lg bg-black">
            {src && <Cropper image={src} crop={crop} zoom={zoom} aspect={OG_ASPECT} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />}
          </div>
          <div className="px-1">
            <Label className="text-xs text-muted-foreground">Zoom</Label>
            <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} className="mt-2" />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid={`${testId}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save image"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OgImageUploader;
